import { Controller } from "@hotwired/stimulus"
import { fetchWithCsrf } from "../util/common"
import PresignedUpload from "../util/presigned_upload"
import { takePendingFile } from "../util/pending_content_upload"

const MAX_FILE_SIZE_MB = 5120 // 5 GB
const VIDEO_MIME_BY_EXTENSION = {
  ".mp4": "video/mp4",
  ".m4v": "video/x-m4v",
  ".mov": "video/quicktime",
  ".avi": "video/x-msvideo",
  ".mkv": "video/x-matroska",
  ".webm": "video/webm",
  ".wmv": "video/x-ms-wmv"
}

// Connects to the data-controller="content-upload"
export default class extends Controller {
  static values = {
    fileUploadsUrl: String,
    createFileUrl: String,
    imageTypes: Array,
    videoTypes: Array,
    imageExtensions: Array,
    videoExtensions: Array
  }

  static targets = [
    "imagePreview",
    "videoPreview",
    "placeholder",
    "progressContainer",
    "progressBar",
    "progressPercentage",
    "statusMessage",
    "errorMessage",
    "cancelButton",
    "uploadButton",
    "fileId",
    "type"
  ]

  upload = null
  previewObjectUrl = null

  connect() {
    const file = takePendingFile()
    if (!file) {
      document.dispatchEvent(new CustomEvent("modal:hide"))
      return
    }

    this.startUpload(file)
  }

  disconnect() {
    if (this.upload) {
      this.upload.cancel()
      this.upload.destroyRecord({ silent: true })
    }

    this.revokePreview()
  }

  startUpload(file) {
    if (this.fileSizeExceedsLimit(file)) {
      this.failUpload(`File size exceeds the maximum allowed size of ${MAX_FILE_SIZE_MB / 1024} GB. Please select a smaller file.`)
      return
    }

    if (!this.isSupportedFormat(file)) {
      this.failUpload(this.getUnsupportedFormatMessage())
      return
    }

    this.beginUploadUi()

    this.isImage(file) ? this.uploadImage(file) : this.uploadVideo(file)
  }

  async uploadImage(file) {
    this.showStatus("Uploading image...")

    if (!this.isHeic(file)) {
      this.showImagePreview(this.previewUrl(file))
    }

    try {
      const content = await this.postImage(file)
      this.showImagePreview(content.file_url)
      this.applyUploaded(content.type, content.file_id)
    }
    catch (error) {
      this.failUpload(error.message)
    }
  }

  async postImage(file) {
    const formData = new FormData()
    formData.append("content[file]", file)

    const response = await fetchWithCsrf(this.createFileUrlValue, {
      method: "POST",
      body: formData,
      headers: { Accept: "application/json" }
    })

    const payload = await response.json().catch(() => ({}))

    if (!response.ok)
      throw new Error((payload.errors || [ "Image upload failed" ]).join(", "))

    return payload
  }

  async uploadVideo(file) {
    this.showVideoPreview(file)
    this.showProgress(true)
    this.upload = new PresignedUpload(this.fileUploadsUrlValue, {
      onProgress: (percentage) => this.setProgress(percentage)
    })

    try {
      const completed = await this.upload.start(file, this.videoContentType(file))
      this.showProgress(false)
      this.applyUploaded("video", completed.uploadable_id)
    }
    catch (error) {
      if (error.name === "AbortError")
        return

      this.upload?.destroyRecord({ silent: true })
      this.failUpload(this.videoErrorMessage(error))
    }
    finally {
      this.upload = null
    }
  }

  cancel(event) {
    event?.preventDefault()

    this.upload?.cancel()
    this.upload?.destroyRecord({ silent: true })
    document.dispatchEvent(new CustomEvent("modal:hide"))
  }

  applyUploaded(type, fileId) {
    this.typeTarget.value = type
    this.fileIdTarget.value = fileId
    this.clearStatus()
    this.setCancelVisible(false)
    this.uploadButtonTarget.disabled = false
  }

  failUpload(message) {
    this.showProgress(false)
    this.clearStatus()
    this.setCancelVisible(false)
    this.uploadButtonTarget.disabled = true
    this.restorePlaceholder()
    this.showError(message)
  }

  restorePlaceholder() {
    this.revokePreview()
    this.imagePreviewTarget.removeAttribute("src")
    this.videoPreviewTarget.removeAttribute("src")
    this.hide(this.imagePreviewTarget)
    this.hide(this.videoPreviewTarget)
    this.show(this.placeholderTarget)
  }

  beginUploadUi() {
    this.clearError()
    this.uploadButtonTarget.disabled = true
    this.setCancelVisible(true)
    this.hide(this.placeholderTarget)
  }

  isSupportedFormat(file) {
    return this.isImage(file) || this.isVideo(file)
  }

  isImage(file) {
    return this.matchesFormat(file, this.imageTypesValue, this.imageExtensionsValue)
  }

  isVideo(file) {
    return this.matchesFormat(file, this.videoTypesValue, this.videoExtensionsValue)
  }

  matchesFormat(file, contentTypes, extensions) {
    return contentTypes.includes(file.type) ||
      extensions.includes(this.fileExtension(file))
  }

  getUnsupportedFormatMessage() {
    const supportedFormats = [ ...this.imageExtensionsValue, ...this.videoExtensionsValue ]
      .map(extension => extension.replace(".", "").toUpperCase())
      .join(", ")

    return `Unsupported file format. Supported formats: ${supportedFormats}`
  }

  videoContentType(file) {
    return VIDEO_MIME_BY_EXTENSION[this.fileExtension(file)] || file.type
  }

  isHeic(file) {
    const extension = this.fileExtension(file)

    return extension === ".heic" || extension === ".heif" ||
      file.type === "image/heic" || file.type === "image/heif"
  }

  fileExtension(file) {
    const name = file.name.toLowerCase()
    const dotIndex = name.lastIndexOf(".")

    return dotIndex === -1 ? "" : name.slice(dotIndex)
  }

  fileSizeExceedsLimit(file) {
    return file.size / (1024 * 1024) > MAX_FILE_SIZE_MB
  }

  showImagePreview(src) {
    this.imagePreviewTarget.src = src
    this.show(this.imagePreviewTarget)
    this.hide(this.videoPreviewTarget)
  }

  showVideoPreview(file) {
    this.videoPreviewTarget.src = this.previewUrl(file)
    this.videoPreviewTarget.load()
    this.show(this.videoPreviewTarget)
    this.hide(this.imagePreviewTarget)
  }

  showProgress(flag) {
    this.progressContainerTarget.classList.toggle("hidden", !flag)

    if (flag)
      this.setProgress(0)
  }

  setProgress(percentage) {
    this.progressBarTarget.style.width = `${percentage}%`
    this.progressPercentageTarget.textContent = `${percentage}%`
  }

  setCancelVisible(flag) {
    this.cancelButtonTarget.classList.toggle("hidden", !flag)
  }

  showStatus(message) {
    this.statusMessageTarget.textContent = message
    this.show(this.statusMessageTarget)
  }

  clearStatus() {
    this.statusMessageTarget.textContent = ""
    this.hide(this.statusMessageTarget)
  }

  showError(message) {
    this.errorMessageTarget.textContent = message
    this.show(this.errorMessageTarget)
  }

  clearError() {
    this.errorMessageTarget.textContent = ""
    this.hide(this.errorMessageTarget)
  }

  videoErrorMessage(error) {
    if (error.message.includes("Upload stalled"))
      return error.message

    return `Video upload failed: ${error.message}. Please, contact support.`
  }

  previewUrl(file) {
    this.revokePreview()
    this.previewObjectUrl = URL.createObjectURL(file)

    return this.previewObjectUrl
  }

  revokePreview() {
    if (!this.previewObjectUrl)
      return

    URL.revokeObjectURL(this.previewObjectUrl)
    this.previewObjectUrl = null
  }

  show(element) {
    element.classList.remove("hidden")
  }

  hide(element) {
    element.classList.add("hidden")
  }
}
